import React, { useEffect, useState } from "react";
import Table from "./Table";
import { CSVLink } from "react-csv";
import { useLazyQuery, useMutation, gql } from "@apollo/client";
import moment from "moment";
import { Spin } from "antd";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import HandleConditions from "./Conditions/HandleConditions";

/* =======================
   GraphQL Queries
======================= */

const DATA_CONDITIONS = gql`
  query ($sortInput: ConditionsSort) {
    conditionsOverview(
      sort: $sortInput
      ){
      results {
        id
        name
      }
    }
  }
`;

const DELETE_CONDITION = gql`
  mutation deleteCondition($id: ID!) {
    deleteCondition(_id: $id) {
      success
      message
    }
  }
`;

/* =======================
   Component
======================= */

export default function Condition() {
  const [displayData, setDisplayData] = useState<any>([]);
  const [sortInput, setSortInput] = useState<object>({ id: -1 });

  const [currentScreen, setCurrentScreen] = useState<"table" | "manage">("table");
  const [editConditionData, setEditConditionData] = useState<any>(null);

  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [deleteCondition] = useMutation(DELETE_CONDITION);

  const [getCondition, { loading, data }] = useLazyQuery<any>(
    DATA_CONDITIONS,
    {
      fetchPolicy: "network-only",
      onCompleted: (completeData) => {
        const tempArr: any[] = [];

        tempArr.push(["ID", "Name", "Description"]);

        completeData.conditionsOverview.results.forEach((value: any) => {
          tempArr.push([
            value.id,
            value.name,
            value.description || "",
          ]);
        });

        setDisplayData(tempArr);
      },
    }
  );

  /* =======================
     Lifecycle
  ======================= */

  useEffect(() => {
    getDefaultData();
  }, []);

  const getDefaultData = () => {
    getCondition({
      variables: {
        sortInput: { id: -1 },
      },
    });
  };

  /* =======================
     Sorting
  ======================= */

  const handleSorting = (index: any) => {
    const sortHeader = ["id", "name"][index];

    const sortOrder =
      sortHeader === "name"
        ? "asc"
        : index === sortField && order === "asc"
        ? "desc"
        : "asc";

    const sortInput = {
      [sortHeader]: sortOrder === "asc" ? 1 : -1,
    };

    setSortField(index);
    setOrder(sortOrder);

    getCondition({
      variables: { sortInput },
    });
  };

  /* =======================
     Delete
  ======================= */

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this condition?"))
      return;

    try {
      const response = await deleteCondition({
        variables: { id },
      });

      if (response.data?.deleteCondition?.success) {
        getDefaultData();
      } else {
        alert(response.data?.deleteCondition?.message || "Delete failed");
      }
    } catch (error: any) {
      alert(error.message || "Something went wrong");
    }
  };

  /* =======================
     UI
  ======================= */

  return (
    <>
      {loading || data === undefined ? (
        <Spin />
      ) : (
        <>
          {currentScreen === "table" ? (
            <>
              {/* Header */}
              <div className="exportuploadbuttonposition">
                <CSVLink
                  data={displayData}
                  filename={`Conditions_${moment().format(
                    "MM/DD/YYYY_HH:mm:ss"
                  )}.csv`}
                  className="export-button"
                >
                  Export
                </CSVLink>

                <button
                  className="primary-button"
                  onClick={() => {
                    setEditConditionData(null);
                    setCurrentScreen("manage");
                  }}
                >
                  Add Condition
                </button>
              </div>

              {/* Table */}
              <Table
                headers={["Condition", "Description", "Actions"]}
                headersSortBool={[true, false, false]}
                data={data.conditionsOverview.results.map((value: any) => [
                  value.name,
                  value.description || "",
                  <div style={{ display: "flex", gap: 12 }}>
                    <EditOutlined
                      style={{ color: "#1677ff", cursor: "pointer" }}
                      onClick={() => {
                        setEditConditionData({
                          _id: value.id,
                          name: value.name,
                          description: value.description,
                        });
                        setCurrentScreen("manage");
                      }}
                    />

                    <DeleteOutlined
                      style={{ color: "red", cursor: "pointer" }}
                      onClick={() => handleDelete(value.id)}
                    />
                  </div>,
                ])}
                handleSorting={handleSorting}
                order={order}
                field={sortField}
              />
            </>
          ) : (
            <HandleConditions
              editConditionData={editConditionData}
              onClose={() => {
                setEditConditionData(null);
                setCurrentScreen("table");
                getDefaultData();
              }}
              refreshData={getDefaultData}
            />
          )}
        </>
      )}
    </>
  );
}